/* Copyright (c) 2024, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

/**
 * Gets the topmost element at the specified position.
 *
 * @param x in viewport coordinate
 * @param y in viewport coordinate
 * @param predicate optional predicate to be applied while searching for the element
 * @returns the element, `undefined` if not found
 */
export function getElementAt(
    x: number,
    y: number,
    predicate?: (element: HTMLElement) => boolean
): HTMLElement | undefined {
    const topElement = document.elementFromPoint(x, y) || undefined;
    if (!topElement) {
        return undefined;
    }

    const isMatch = (e: Element): e is HTMLElement => e instanceof HTMLElement && (!predicate || predicate(e));

    if (isMatch(topElement)) {
        return topElement;
    }

    // optimization - find lower elements only when needed
    const elements = document.elementsFromPoint(x, y).slice(1);
    return elements.find(element => isMatch(element)) as HTMLElement;
}
